import i18n from "../i18n";

export const MAP_ZOOM_STEP = 0.5;
const WHEEL_NOTCH_PX = 100;
const MAX_WHEEL_DELTA = 1;

export function zoomDeltaFromWheel(deltaY, deltaMode) {
  let delta = Number(deltaY) || 0;
  if (deltaMode === 1) delta *= 16;
  if (deltaMode === 2) delta *= 400;
  const raw = -(delta / WHEEL_NOTCH_PX) * MAP_ZOOM_STEP;
  if (raw > MAX_WHEEL_DELTA) return MAX_WHEEL_DELTA;
  if (raw < -MAX_WHEEL_DELTA) return -MAX_WHEEL_DELTA;
  return raw;
}

export function nextGradualZoom(current, delta, minZoom, maxZoom) {
  const next = (Number(current) || 0) + (Number(delta) || 0);
  const min = Number.isFinite(minZoom) ? minZoom : 0;
  const max = Number.isFinite(maxZoom) ? maxZoom : 22;
  const clamped = Math.min(max, Math.max(min, next));
  return Math.round(clamped * 1000) / 1000;
}

export function withGradualZoomOptions(options = {}) {
  return {
    ...options,
    isFractionalZoomEnabled: true,
    zoomControl: false,
    scrollwheel: false,
  };
}

function readLimit(map, name, fallback) {
  const value = map && map[name];
  return Number.isFinite(value) ? value : fallback;
}

function ensureZoomStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("ac-map-zoom-styles")) return;
  const style = document.createElement("style");
  style.id = "ac-map-zoom-styles";
  style.textContent = [
    ".ac-map-zoom{display:flex;flex-direction:column;margin:0 10px 8px 0;",
    "border-radius:2px;overflow:hidden;box-shadow:0 1px 4px rgba(0,0,0,.3);}",
    ".ac-map-zoom__btn{width:40px;height:40px;margin:0;padding:0;border:0;",
    "background:#fff;color:#666;font-size:22px;line-height:40px;cursor:pointer;}",
    ".ac-map-zoom__btn + .ac-map-zoom__btn{border-top:1px solid #e6e6e6;}",
    ".ac-map-zoom__btn:hover{background:#f5f5f5;color:#333;}",
  ].join("");
  document.head.appendChild(style);
}

function applyZoomNow(map, zoom) {
  if (typeof map.moveCamera === "function") {
    map.moveCamera({ zoom });
    return;
  }
  map.setZoom(zoom);
}

function createZoomBy(map) {
  let pending = 0;
  let frame = 0;

  function flush() {
    frame = 0;
    const delta = pending;
    pending = 0;
    if (!delta || !map.getZoom) return;
    const current = map.getZoom();
    const next = nextGradualZoom(
      current,
      delta,
      readLimit(map, "minZoom", 0),
      readLimit(map, "maxZoom", 22)
    );
    if (next === current) return;
    applyZoomNow(map, next);
  }

  return function zoomBy(delta) {
    if (!delta) return;
    pending += delta;
    if (typeof requestAnimationFrame !== "function") {
      flush();
      return;
    }
    if (!frame) frame = requestAnimationFrame(flush);
  };
}

export function applyGradualMapZoom(map) {
  if (!map || map.__acGradualZoom || !map.getDiv || !map.setOptions) return;
  map.__acGradualZoom = true;
  map.setOptions({
    isFractionalZoomEnabled: true,
    zoomControl: false,
    scrollwheel: false,
  });
  const zoomBy = createZoomBy(map);

  const surface = map.getDiv();
  if (surface) {
    surface.addEventListener(
      "wheel",
      (event) => {
        if (!event || event.deltaY == null) return;
        event.preventDefault();
        event.stopPropagation();
        zoomBy(zoomDeltaFromWheel(event.deltaY, event.deltaMode));
      },
      { passive: false, capture: true }
    );
  }

  if (!map.controls || typeof document === "undefined") return;
  const mapsApi =
    (typeof window !== "undefined" && window.google && window.google.maps) ||
    null;
  const position =
    mapsApi && mapsApi.ControlPosition
      ? mapsApi.ControlPosition.RIGHT_BOTTOM
      : 9;
  if (!map.controls[position]) return;

  ensureZoomStyles();
  const control = document.createElement("div");
  control.className = "ac-map-zoom";

  const zoomIn = document.createElement("button");
  zoomIn.type = "button";
  zoomIn.className = "ac-map-zoom__btn";
  zoomIn.textContent = "+";
  zoomIn.title = i18n.t("mapZoomIn");
  zoomIn.setAttribute("aria-label", i18n.t("mapZoomIn"));
  zoomIn.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    zoomBy(MAP_ZOOM_STEP);
  });

  const zoomOut = document.createElement("button");
  zoomOut.type = "button";
  zoomOut.className = "ac-map-zoom__btn";
  zoomOut.textContent = "−";
  zoomOut.title = i18n.t("mapZoomOut");
  zoomOut.setAttribute("aria-label", i18n.t("mapZoomOut"));
  zoomOut.addEventListener("click", (event) => {
    event.preventDefault();
    event.stopPropagation();
    zoomBy(-MAP_ZOOM_STEP);
  });

  control.appendChild(zoomIn);
  control.appendChild(zoomOut);
  map.controls[position].push(control);
}
